import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * List SPA Sources Step
 *
 * Reads previously registered SpaSource records (written by
 * `upsert_spa_source`) so a downstream step — typically a browser agent —
 * can be told which vendor files were already ingested and should not be
 * downloaded again.
 *
 * Example workflow:
 * ```
 * List SPA Sources (vendor: "hubbell") → Browser Use Agent (dataInputs: knownFiles)
 *   → Upsert SPA Source (for each newly downloaded file)
 * ```
 */
export const listSpaSourcesStep = defineStepConfig({
  type: "list_spa_sources",
  name: "List SPA Sources",
  description:
    "List previously registered SPA source files for a vendor, so downstream steps (e.g. a browser agent) can avoid re-downloading them.",
  category: "Data Access",
  libraryModule: "Pricing",

  inputSchema: z.object({
    vendor: z
      .string()
      .optional()
      .describe(
        "Vendor key to filter by, e.g. 'hubbell'. Leave blank to list sources for all vendors.",
      ),
  }),

  outputSchema: z.object({
    // Wrapped in a plain object (rather than a bare array) so this socket
    // infers as a single "json" blob instead of "array<json>" — wiring it
    // into a downstream step (e.g. a browser agent prompt) must pass the
    // whole list as one value, not trigger a per-item fanout.
    sources: z
      .object({
        items: z.array(
          z.object({
            spaSourceId: z.string().uuid(),
            fileId: z.string().uuid(),
            fileName: z.string().nullable(),
            vendor: z.string(),
            metadata: z.record(z.string(), z.unknown()),
            createdAt: z.string().describe("ISO timestamp"),
          }),
        ),
      })
      .describe(
        "Previously registered SPA source files, newest first, as a single JSON object (not an array socket).",
      ),
    count: z.number().describe("Number of sources returned"),
  }),

  configSchema: z.object({
    spaRequestId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Optionally filter to sources linked to a specific SpaRequest.",
      ),
    limit: z
      .number()
      .int()
      .min(1)
      .max(5000)
      .prefault(1000)
      .describe("Max sources to return."),
  }),

  emits: [] as const,
});
